// Centralised content for Theta Dynamics website

export const COMPANY = {
  name: "Theta Dynamics Pvt. Ltd.",
  shortName: "Theta Dynamics",
  tagline: "Engineering Secure, Intelligent & Immersive Digital Solutions.",
  pitch:
    "Theta Dynamics delivers solutions across Cyber Security, AI/ML, ERP, AR/VR and Surveillance — engineering resilient platforms for enterprises, governments and institutions.",
  email: "contact@thetadynamics.in",
  phones: ["+91-90084-50948", "+91-70197-13085"],
  address: "1st Floor, Sairaj Landmark, Savarkar Rd, Tilakwadi",
  city: "Belagavi, Karnataka 590006, India",
  hours: "Mon – Fri · 9:00 AM – 6:00 PM IST",
  social: {
    linkedin: "https://www.linkedin.com/company/thetadynamics/",
    newsletter: "https://www.linkedin.com/newsletters/theta-insights-7461434056577032192/",
    twitter: "https://x.com/thetadynamics",
    github: "https://github.com/thetadynamics",
  },
};

export const STATS = [
  { value: "100+", label: "Projects Delivered" },
  { value: "50+", label: "Happy Clients" },
  { value: "25+", label: "Engineers & Experts" },
  { value: "15+", label: "Countries Served" },
  { value: "5+", label: "Years of Operation" },
];

export const HERO_SLIDES = [
  {
    eyebrow: "Strategic MoU",
    title: "Theta Dynamics × NASSCOM Sector Skill Council — FutureSkills.",
    subtitle:
      "A formal collaboration to power the next generation of cyber, AI and cloud talent for India's digital economy.",
    image:
      "https://images.pexels.com/photos/7794060/pexels-photo-7794060.jpeg",
  },
  {
    eyebrow: "Odoo Partner",
    title: "An official Odoo Partner for ERP across India and the Middle East.",
    subtitle:
      "Implementing, customising and operating Odoo for manufacturers, contractors, NGOs and retailers — at enterprise scale.",
    image:
      "https://images.pexels.com/photos/1181320/pexels-photo-1181320.jpeg",
  },
  {
    eyebrow: "TiE Global Summit 2026",
    title: "Recognised in TGS100 — Top 100 Investable Startups, India.",
    subtitle:
      "Selected by TiE Global among India's most-investable startups for our work in Cyber Security, AI and ERP.",
    image:
      "https://images.pexels.com/photos/17323801/pexels-photo-17323801.jpeg",
  },
  {
    eyebrow: "Product · Anciens Network",
    title: "Anciens Network — modern alumni management for institutions.",
    subtitle:
      "A multi-tenant platform powering verified alumni directories, events, donations and mentorship at scale.",
    image:
      "https://images.unsplash.com/photo-1628495073765-e7fdbb7c25fc",
  },
  {
    eyebrow: "Product · Mock Exam Platform",
    title: "Mock-exam engine for CET & competitive exams.",
    subtitle:
      "An adaptive testing platform powering rank prediction, mock counselling and exam practice — used by lakhs of aspirants.",
    image:
      "https://images.unsplash.com/photo-1722080826167-4ea87368cbc5",
  },
];

export const SERVICES = [
  {
    slug: "cybersecurity",
    name: "Cyber Security",
    short:
      "VAPT, audits, threat-hunting, OSINT and incident response — operator-grade.",
    description:
      "Cyber Security is at the core of everything we ship. From black-box VAPT and red-team exercises to ISO 27001 readiness and 24×7 monitoring, we are an end-to-end security partner for enterprises, governments and NGOs.",
    deliverables: [
      "Vulnerability Assessment & Penetration Testing (VAPT)",
      "Web / Mobile / API Security Audits",
      "Network Security & Hardening",
      "Threat Detection, SOC Setup & SIEM",
      "Incident Response & Digital Forensics",
      "OSINT & Security Awareness Training",
    ],
    stack: ["OWASP", "ISO 27001", "NIST", "Burp Suite", "Metasploit", "Nessus"],
    icon: "shield",
    image:
      "https://images.unsplash.com/photo-1760199789455-49098afd02f0",
  },
  {
    slug: "ai-ml",
    name: "AI / ML",
    short: "Predictive analytics, computer vision, NLP and intelligent automation.",
    description:
      "We translate raw data into measurable decisions. Theta Dynamics designs ML pipelines, computer vision systems and NLP products — and operationalises them with MLOps, model monitoring and clear cost discipline.",
    deliverables: [
      "Predictive Analytics & Forecasting",
      "Computer Vision Solutions",
      "Natural Language Processing & Chatbots",
      "Recommendation & Anomaly Detection",
      "MLOps & Model Monitoring",
      "Generative AI Integrations",
    ],
    stack: ["PyTorch", "TensorFlow", "scikit-learn", "OpenCV", "spaCy", "HuggingFace"],
    icon: "brain",
    image:
      "https://images.unsplash.com/photo-1722080826167-4ea87368cbc5",
  },
  {
    slug: "erp",
    name: "Odoo & ERPNext",
    short: "Official Odoo Partner + ERPNext expertise — implementation, customisation & migration.",
    description:
      "Run your manufacturing, retail, real-estate, education or services business on a unified ERP. As an official Odoo Partner with deep ERPNext / Frappe expertise, we engineer modules, integrate third-party systems, migrate legacy data and train your teams — across both ecosystems.",
    deliverables: [
      "Odoo Implementation (Community & Enterprise)",
      "ERPNext / Frappe Implementation & Hosting",
      "Custom Module & Frappe App Development",
      "Business Process Automation & Workflows",
      "Third-party Integrations (POS, payments, logistics)",
      "ERP Migration, Version Upgrades & Data Migration",
      "User Training & 24×7 Support Retainers",
    ],
    stack: ["Odoo 17", "ERPNext 15", "Frappe", "Python", "PostgreSQL", "MariaDB", "REST API"],
    icon: "package",
    image:
      "https://images.pexels.com/photos/7794060/pexels-photo-7794060.jpeg",
  },
  {
    slug: "ar-vr",
    name: "AR / VR",
    short:
      "Immersive training, simulation and visualisation experiences.",
    description:
      "Theta Dynamics builds AR and VR products for training, industrial simulation, real-estate visualisation and edtech. We engineer cross-device experiences — from WebXR to native Unity / Unreal apps — with photoreal assets and physics-correct interactions.",
    deliverables: [
      "AR / VR Training Simulators",
      "Industrial Twin & Walk-through Experiences",
      "WebXR Product Configurators",
      "Real-estate & Architecture Visualisation",
      "Immersive Education & EdTech",
      "Cross-device (Quest, Vision Pro, Mobile AR)",
    ],
    stack: ["Unity", "Unreal", "WebXR", "Three.js", "ARKit", "Blender"],
    icon: "vr",
    image:
      "https://images.unsplash.com/photo-1628495073765-e7fdbb7c25fc",
  },
  {
    slug: "surveillance",
    name: "Surveillance",
    short: "AI-driven video analytics, perimeter security and smart-city deployments.",
    description:
      "We engineer surveillance solutions that go beyond CCTV — combining AI-powered video analytics, edge inferencing, ANPR, intrusion detection and integration with command-and-control dashboards for enterprises and city-scale deployments.",
    deliverables: [
      "AI Video Analytics (people, vehicle, behaviour)",
      "ANPR & Access Control Integration",
      "Perimeter & Intrusion Detection",
      "Edge Deployment & Camera Tuning",
      "Command & Control Dashboards",
      "VMS Integration (Milestone, Genetec, etc.)",
    ],
    stack: ["DeepStream", "OpenCV", "YOLO", "ONVIF", "Kafka", "RTSP"],
    icon: "camera",
    image:
      "https://images.pexels.com/photos/17323801/pexels-photo-17323801.jpeg",
  },
  {
    slug: "custom-software",
    name: "Custom Software & Apps",
    short:
      "Bespoke web, mobile and backend systems engineered for scale.",
    description:
      "From category-defining SaaS to internal tooling, we design, build and ship maintainable software. Modern stacks, clean architecture, observability built in — and a security review on every release.",
    deliverables: [
      "Custom Web Applications",
      "Cross-platform Mobile Apps",
      "REST & GraphQL APIs",
      "E-commerce & Marketplaces",
      "Progressive Web Apps (PWA)",
      "Legacy Modernisation",
    ],
    stack: ["React", "Node.js", "Python", "FastAPI", "PostgreSQL", "MongoDB"],
    icon: "code",
    image:
      "https://images.unsplash.com/photo-1628495073765-e7fdbb7c25fc",
  },
];

export const PRODUCTS = [
  {
    slug: "karnatakacet",
    name: "KarnatakaCET.com",
    tagline: "India's most-used CET counselling & rank predictor.",
    summary:
      "An end-to-end platform serving Karnataka CET aspirants — rank prediction, college & branch predictor, mock counselling, and mentorship — built and operated by Theta Dynamics.",
    features: [
      "Real-time rank-to-college predictor based on previous-year cutoffs",
      "Personalised college & branch shortlisting",
      "Mock seat allotment & document checklist",
      "Mentor-led counselling for engineering & medical aspirants",
      "Mobile-first PWA used by lakhs of students every cycle",
    ],
    metrics: [
      { v: "10L+", l: "Sessions / cycle" },
      { v: "50K+", l: "Predictions Served" },
      { v: "98%", l: "Allotment Accuracy" },
    ],
    image:
      "https://images.pexels.com/photos/7794060/pexels-photo-7794060.jpeg",
    cta: "Visit karnatakacet.com",
    link: "https://karnatakacet.com",
  },
  {
    slug: "alumni-management",
    name: "Anciens Network",
    tagline: "Modern alumni management platform for institutions.",
    summary:
      "Anciens Network is a multi-tenant SaaS that helps universities, colleges and schools digitise their alumni network — verified directory, events, donations, jobs and mentorship in one place.",
    features: [
      "Branded alumni portals with SSO & verified profiles",
      "Events with RSVP, ticketing and live streams",
      "Donations, scholarships & fundraising campaigns",
      "Jobs board and mentorship matching",
      "Powerful admin analytics & engagement scoring",
    ],
    metrics: [
      { v: "20+", l: "Institutions Onboarded" },
      { v: "75K+", l: "Alumni Profiles" },
      { v: "₹2Cr+", l: "Donations Processed" },
    ],
    image:
      "https://images.pexels.com/photos/1181320/pexels-photo-1181320.jpeg",
    cta: "Request a Demo",
    link: "/contact",
  },
];

export const USE_CASES = [
  {
    client: "Merlin Hydraulics",
    sector: "Manufacturing · India · Web & Cyber Security",
    challenge:
      "Merlin Hydraulics' corporate website was compromised and the domain was blacklisted by Google's Safe Browsing — visitors saw a red interstitial, and email deliverability and SEO rankings collapsed.",
    solution:
      "Forensic triage of the compromised stack, full malware clean-up, hardening of the CMS and hosting environment, removal of injected payloads, file-integrity monitoring, and a formal Google reconsideration / blacklist-removal request.",
    impact:
      "Site cleaned, domain successfully removed from Google's blacklist, Safe Browsing warning lifted within days, and ongoing monitoring put in place so it doesn't happen again.",
    tags: ["Cyber Security", "Web", "Incident Response"],
  },
  {
    client: "BG Godse Constructions Pvt. Ltd.",
    sector: "Government Civil Contracting · India",
    challenge:
      "1000+ labourers, 40+ vehicles, manual stock-keeping for bitumen & aggregates, and salary leakage running into lakhs every month.",
    solution:
      "Custom Odoo rollout: Fleet, Inventory, Payroll, Manufacturing, Project Management & Subcontracting — tied together with site-activity tracking.",
    impact:
      "Single source of truth across sales, accounting, logistics and warehouses — leadership now monitors all activity in one tap.",
    tags: ["Odoo ERP", "Construction", "Fleet"],
  },
  {
    client: "Clicktronics",
    sector: "Retail e-Commerce · Karnataka, India",
    challenge:
      "3,500+ SKUs across Canon, HP, Dell, Secureeye etc. — inventory, sales and customer queries handled manually across multiple stores.",
    solution:
      "End-to-end CRM, Inventory, Sales, Purchase, Helpdesk, Service & Repairs and an integrated e-commerce front, all on Odoo.",
    impact:
      "Broke business silos; sales, accounting and warehouse aligned. Traceability and accountability across every order.",
    tags: ["Odoo ERP", "Retail", "e-Commerce"],
  },
  {
    client: "Hadiyah",
    sector: "NGO · Kingdom of Saudi Arabia",
    challenge:
      "700+ volunteers, multiple donation platforms, no donor tracking and zero accountability across distributions.",
    solution:
      "Unified Odoo with Donation Mgmt, CRM, Helpdesk, Events, Purchase, Inventory, Manufacturing, Fleet, HRMS and Accounts.",
    impact:
      "All donation flows consolidated; live dashboards drove better decisions and transparent reporting to stakeholders.",
    tags: ["Odoo ERP", "NGO", "Dashboards"],
  },
  {
    client: "Digital World",
    sector: "Electronics Manufacturing · Riyadh, KSA",
    challenge:
      "BOM, scrap and distribution tracked on paper; no reporting tools for assembly, warehousing or logistics.",
    solution:
      "Integrated CRM, Sales, Purchase, Accounting, Manufacturing, Inventory, HRMS and Documents Management.",
    impact:
      "One-stop ERP that connected sales, billing, warehouse, inventory and shipping — a true digital backbone.",
    tags: ["Odoo ERP", "Manufacturing"],
  },
  {
    client: "Annamayya District Police",
    sector: "Government · Andhra Pradesh, India",
    challenge:
      "Officers needed practical OSINT and surveillance capabilities to investigate cyber-enabled crimes and digital fraud.",
    solution:
      "Custom OSINT engagement — open-source intelligence tools, social-media forensics and case-study driven labs delivered by our security team.",
    impact:
      "Officers up-skilled to triage and investigate digital evidence — recognised with a formal felicitation by the department.",
    tags: ["Cyber Security", "Surveillance", "OSINT"],
  },
  {
    client: "Karnataka Engineering Aspirants",
    sector: "EdTech · India",
    challenge:
      "Aspirants and parents had no reliable way to predict CET ranks, branch cut-offs or simulate counselling rounds.",
    solution:
      "Built KarnatakaCET.com — a mobile-first counselling and prediction platform with a custom recommendation engine and adaptive mock-exam engine.",
    impact:
      "Lakhs of students rely on the platform every CET cycle. Mentor-led cohorts, near-perfect allotment matching.",
    tags: ["Custom Software", "AI/ML", "EdTech"],
  },
];

export const TEAM = [
  {
    name: "Mr. Gajendra Deshpande",
    role: "Founder & Managing Director",
    bio: "Technologist and security practitioner. Leads Theta Dynamics' product, security and AI engineering — shipping platforms used by enterprises, governments and institutions across India and abroad.",
    avatarLetter: "GD",
    image: "https://thetadynamics.in/assets/images/team/gcd.png",
  },
  {
    name: "Mr. Pradosh Hegde",
    role: "Co-founder & Director, Business Operations",
    bio: "Drives partnerships, growth and delivery across the Theta Dynamics portfolio — making sure every engagement scales from sale to support without friction.",
    avatarLetter: "PH",
    image: "https://thetadynamics.in/assets/images/team/pradosh.png",
  },
  {
    name: "Mr. Omkar Deshpande",
    role: "Director, ERP",
    bio: "Heads the ERP practice — Odoo and ERPNext implementations across manufacturing, contracting, retail and NGOs. Owns delivery quality, customisation and customer success.",
    avatarLetter: "OD",
    image: "/assets/omkar.png",
  },
  {
    name: "Dr. Udaya Shankar Puranik",
    role: "Advisor",
    bio: "Senior advisor providing strategic and technological guidance to Theta Dynamics across cyber security, AI and emerging technology programmes.",
    avatarLetter: "UP",
    image: "https://thetadynamics.in/assets/images/team/puranic.png",
  },
];

export const CERTIFICATIONS = [
  "NASSCOM Member",
  "Odoo Partner",
  "AICTE",
  "VTU",
  "SIDM",
  "Start-up India",
  "MSME Registered",
  "Start-up Karnataka",
  "Google Startup Grant",
  "TiE TGS100",
  "Karnataka Elevate Finalist",
  "DSCI Member",
  "ISO 27001 Aligned",
];

export const CLIENT_LOGOS = [
  "BG Godse Constructions",
  "Clicktronics",
  "Hadiyah",
  "Digital World",
  "Galaxy Machinery",
  "Dhaneshwar Construction",
  "Belgaum Education Society",
  "Bagalkot University",
  "Merlin Hydraulics",
  "P.B. Engineering",
  "Insurance Europ",
  "Durga Metal Profiles",
  "Karnatak Lingayat Education Society",
  "Annamayya District Police",
];
